import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  CircularProgress,
  Typography,
  IconButton,
  TablePagination,
  TextField,
  MenuItem,
  Grid,
  Button,
  Tooltip,
  Popover,
} from "@mui/material";

import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import RefreshIcon from "@mui/icons-material/Refresh";

const AuditLog = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [filters, setFilters] = useState({
    search: "",
    country: "",
    status: "",
    startDate: "",
    endDate: "",
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const API_URL = "https://peacekeeper.duredemos.com/serviceuat/failure-and-retry-logs";

  // ✅ Fetch API
  useEffect(() => {
    const fetchAuditLogs = async () => {
      try {
        const response = await fetch(API_URL);
        if (!response.ok) throw new Error("Failed to fetch audit logs");

        const result = await response.json();

        if (result.success && result.data) {
          const failure = result.data.failureLogs || [];
          const completed = result.data.completedAfterFailure || [];

          const mergedLogs = [...failure, ...completed].sort(
            (a, b) =>
              new Date(b.created_at) - new Date(a.created_at)
          );

          setAuditLogs(mergedLogs);
        } else {
          setAuditLogs([]);
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load audit logs.");
      } finally {
        setLoading(false);
      }
    };

    fetchAuditLogs();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [filters]);

  const countryOptions = useMemo(() => {
    const countries = auditLogs.map((log) => log.country);
    return [...new Set(countries.filter(Boolean))];
  }, [auditLogs]);

  // ✅ Updated Filter Logic (Date Range)
  const filteredLogs = useMemo(() => {
    let filtered = [...auditLogs];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((log) =>
        log.username?.toLowerCase().includes(searchLower) ||
        log.email?.toLowerCase().includes(searchLower) ||
        log.payment_intent_id?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.country) {
      filtered = filtered.filter(
        (log) => log.country === filters.country
      );
    }

    if (filters.status) {
      filtered = filtered.filter(
        (log) =>
          log.status?.toLowerCase() ===
          filters.status.toLowerCase()
      );
    }

    if (filters.startDate && filters.endDate) {
      const start = new Date(filters.startDate);
      const end = new Date(filters.endDate);
      end.setHours(23, 59, 59, 999);

      filtered = filtered.filter((log) => {
        const created = new Date(log.created_at);
        return created >= start && created <= end;
      });
    }

    return filtered;
  }, [auditLogs, filters]);

  const paginatedLogs = filteredLogs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ px: 3, py: 2, width: "100%" }}>

      {/*  FILTER SECTION */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Username, Email, Payment ID"
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
            />
          </Grid>

          <Grid item xs={12} md={2} sx={{width:100}}> 
            <TextField
              select
              fullWidth
              size="small"
              label="Country"
              value={filters.country}
              onChange={(e) =>
                setFilters({ ...filters, country: e.target.value })
              }
            >
              <MenuItem value="">All</MenuItem>
              {countryOptions.map((country, i) => (
                <MenuItem key={i} value={country}>
                  {country}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={2} sx={{width:100}}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Failure">Failure</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
            </TextField>
          </Grid>

          {/* DATE RANGE BUTTON */}
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<CalendarTodayIcon />}
              onClick={(e) => setAnchorEl(e.currentTarget)}
            >
              {filters.startDate && filters.endDate
                ? `${filters.startDate} - ${filters.endDate}`
                : "Select date range"}
            </Button>

            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            >
              <Box p={3} width={300}>
                <Typography mb={2}>
                  Select Date Range
                </Typography>

                <TextField
                  type="date"
                  label="Start Date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={filters.startDate}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      startDate: e.target.value,
                    })
                  }
                  sx={{ mb: 2 }}
                />

                <TextField
                  type="date"
                  label="End Date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={filters.endDate}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      endDate: e.target.value,
                    })
                  }
                />
              </Box>
            </Popover>
          </Grid>
<Grid item xs="auto">
  <Tooltip title="Reset Filters">
    <IconButton
      onClick={() =>
        setFilters({
          search: "",
          country: "",
          status: "",
          startDate: "",
          endDate: "",
        })
      }
      sx={{
        border: "1px solid #ddd",
        borderRadius: 2,
        height: 40,
        width: 40,
      }}
    >
      <RefreshIcon />
    </IconButton>
  </Tooltip>
</Grid>

        </Grid>
      </Paper>

      {/* ✅ TABLE */}
      {loading && <CircularProgress />}
      {error && <Typography color="error">{error}</Typography>}

      {!loading && !error && (
        <Paper elevation={3} sx={{ borderRadius: 3 }}>
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table stickyHeader size="small" sx={{ minWidth: 1400 }}>
              <TableHead>
                <TableRow>
                  {[
                    "Username",
                    "Email",
                    "Country",
                    "Amount",
                    "Status",
                    "Failure Reason",
                    "Cancellation Reason",
                    "Payment Intent ID",
                    "Created At",
                  ].map((head) => (
                    <TableCell
                      key={head}
                      sx={{
                        fontWeight: 600,
                        backgroundColor: "#f4f6f8",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedLogs.map((log, index) => {
                  const isFailure =
                    log.status?.toLowerCase() === "failure";

                  return (
                    <TableRow key={index} hover>
                      <TableCell>{log.username}</TableCell>
                      <TableCell>{log.email}</TableCell>
                      <TableCell>{log.country}</TableCell>
                      <TableCell>{log.amount}</TableCell>

                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-block",    
                            borderRadius: 2,
                            fontSize: 12,
                            fontWeight: 600,
                            backgroundColor: isFailure
                              ? "#fdecea"
                              : "#e8f5e9",
                            color: isFailure
                              ? "#d32f2f"
                              : "#2e7d32",
                          }}
                        >
                          {log.status}
                        </Box>
                      </TableCell>

                      <TableCell>{log.failure_reason}</TableCell>
                      <TableCell>{log.cancellation_reason}</TableCell>
                      <TableCell>{log.payment_intent_id}</TableCell>
                      <TableCell>
                        {new Date(log.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredLogs.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </Paper>
      )}
    </Box>
  );
};
export default AuditLog;
